import { useState, useEffect } from "react";
import { Building2, RotateCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCheckInContext, useCheckInSend } from "../context";
import { docCirrusRpcClient } from "../../../../../lib/rpc/doc-cirrus.client";
import { useAudio } from "../../../../../lib/use-audio";
import { frontdeskDiagnosticLogger } from "../../../../../lib/diagnostic-logger";
import { KioskStep } from "../../../../../components/kiosk-step";
import {
  KioskLoadingPanel,
  KioskStatePanel,
} from "../../../../../components/kiosk-state-panel";

/**
 * Checking in step - performs the actual check-in API call.
 *
 * A failure never routes into the no-appointment branch: the patient can try
 * again or ask the reception for help (KC-19).
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <CheckingInStep />
 * ```
 */
export function CheckingInStep() {
  const { t } = useTranslation();
  const send = useCheckInSend();
  const context = useCheckInContext();
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // Play audio on mount
  useAudio("checkIn-checkingIn");

  useEffect(function performCheckInAttempt() {
    let active = true;
    const performCheckIn = async () => {
      if (!context.patientId) return;

      try {
        const result = await docCirrusRpcClient.frontdesk.checkIn({
          patientId: context.patientId,
          // Carry the eGK attestation so a successful CHECKED_IN commits it server-side. Absent for
          // manual flows, which the server treats as ineligible for the patient-info review.
          ...(context.egkAttestation === undefined
            ? {}
            : { egkAttestation: context.egkAttestation }),
        });
        if (!active) return;

        if (result.status === "CHECKED_IN") {
          send({ type: "CHECKED_IN", scheduleId: result.scheduleId });
        } else if (result.status === "SELECT_APPOINTMENT") {
          send({
            type: "SELECT_APPOINTMENT",
            appointments: [...result.appointments],
          });
        } else if (result.status === "NO_APPOINTMENT") {
          send({ type: "NO_APPOINTMENT" });
        }
      } catch (err) {
        frontdeskDiagnosticLogger.error("Check-in failed", {
          error: err,
          hasPatientId: Boolean(context.patientId),
          attempt,
        });
        if (active) setFailed(true);
      }
    };

    void performCheckIn();
    return function ignoreStaleCheckInAttempt() {
      active = false;
    };
  }, [attempt, context.patientId, context.egkAttestation, send]);

  if (failed) {
    return (
      <KioskStep
        width="narrow"
        align="center"
        title={t("self-service.check-in.checking-in.error-title")}
        primaryAction={{
          label: t("common.retry"),
          icon: RotateCw,
          onClick: () => {
            setFailed(false);
            setAttempt((current) => current + 1);
          },
          testId: "checking-in-retry",
        }}
        secondaryAction={{
          label: t("self-service.help-reception"),
          icon: Building2,
          onClick: () => send({ type: "RECEPTION_HELP" }),
          testId: "checking-in-reception",
        }}
      >
        <KioskStatePanel
          tone="error"
          title={t("self-service.check-in.checking-in.error-v2")}
          body={t("self-service.check-in.checking-in.error-body")}
          testId="frontdesk-checking-in-error"
        />
      </KioskStep>
    );
  }

  return (
    <KioskStep
      width="narrow"
      align="center"
      title={t("self-service.check-in.checking-in.title-v2")}
    >
      <KioskLoadingPanel
        title={t("self-service.check-in.checking-in.loading")}
        body={t("self-service.check-in.checking-in.subtitle-v2")}
        testId="checking-in-loader"
      />
    </KioskStep>
  );
}
